import { useState, useMemo, useEffect } from "react";

import { useProductStore } from "./stores/productStore";
import { useForecastStore } from "./stores/forecastStore";

import { useProducts } from "../model/useProducts";
import { usePredictions } from "../model/usePredictions"
import { useForecast } from "../model/useForecasts";

import { i18n } from "../translate/i18n";

import { futureLinesDefition, defineFutureMonthsData } from "./strategy/usePredictionsViewModel.strategy";
import { PREDICTON_COLORS } from "../components/components.constants";

const xLabels = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const cutInto12MonthsArrays = (arr) => {
    const result = [];
    let i = 0;
    while (i < arr.length) {
        result.push(arr.slice(i, i + 12));
        i += 12;
    }
    return result;
};

const adjustConfidenceIntervalToLatestYear = (confidenceIntervalMap = {}) => {
    const byMonth = {};
    
    for (const key of Object.keys(confidenceIntervalMap)) {
        const [month, yearRaw] = key.split('/');
        const year = Number(yearRaw.length === 2 ? '20' + yearRaw : yearRaw);
        
        if (!byMonth[year]) byMonth[year] = {};
        byMonth[year][month] = confidenceIntervalMap[key];
    }

    const months = [
        'jan', 'fev', 'mar', 'abr', 'mai', 'jun', 
        'jul', 'ago', 'set', 'out', 'nov', 'dez'
    ];

    const confidenceInterval = [];

    for (const month of months) {
        let value = null;
        
        if (byMonth[2026]?.[month]) {
            value = byMonth[2026][month];
        }

        else if (byMonth[2025]?.[month]) {
            value = byMonth[2025][month];
        }
        
        if (value) {
            confidenceInterval.push(value);
        }
    }

    return confidenceInterval;
};

export const useAnalysisViewModel = ({
    shouldFetchPredictionByProduct = false,
    shouldFetchConsolidatedIndexes = false,
    shouldFetchLastSixMonthsPredictions = false,
    shouldFetchConsolidatedPredictions = false,

    shouldFetchProducts = false,
    shouldFetchFiltersAndDriversOptions = false,
    storeId,

    shouldFetchForecastData = false,
    
    clientPlanValuesFromPastMonths,
    enableAdvancedLines,
    predictionChartLocation,

    productId
}) => {

    const { isLoading: isLoadingForecast } = useForecast({ shouldFetchForecastData: shouldFetchForecastData });
    const { 
        products, 
        isLoadingProducts 
    } = useProducts({ 
        shouldFetchProducts: shouldFetchProducts, 
        storeId: storeId,
        shouldFetchFiltersAndDriversOptions: shouldFetchFiltersAndDriversOptions
    });

    const groupFiltersAndDriversOptions = useProductStore((s) => s.groupFiltersAndDriversOptions);
    const latestForecast = useForecastStore((s) => s.latestForecast);

    const { t } = i18n

    const { 
      fetchLastSixMonthsPredictions: {
        data: fetchLastSixMonthsPredictions,
        isLoading
      },

      productPredictionsData: {
        data: productPredictionsData,
        isLoadingProductPredictions
      },

      indexes: {
        data: fetchConsolidatedIndexes,
        isLoadingIndexes
      },

      consolidated: {
        data: fetchConsolidatedPredictions,
        isLoading: isLoadingConsolidatedPredictions
      },
    } = usePredictions(productId, { 
      shouldFetchPredictionByProduct,
      shouldFetchConsolidatedIndexes,
      shouldFetchLastSixMonthsPredictions,
      shouldFetchConsolidatedPredictions
    });

    const [selectedValue, setSelectedValue] = useState(1);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [productIds, setProductIds] = useState([]);
    const [freezeColumns, setFreezeColumns] = useState(true);

    const selected = useMemo(() => {
        return groupFiltersAndDriversOptions.find(option => option.value === selectedValue) || {};
    }, [groupFiltersAndDriversOptions, selectedValue]);

    const generationDate = useMemo(() => {
        return new Date(latestForecast.createdAt).toLocaleDateString(i18n.language, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        });
    }, [latestForecast.createdAt]);

    const groupingName = useMemo(() => {
        return selected.groupDisplayName || '';
    }, [selected.groupDisplayName])

    useEffect(() => {
        if (selected?.productIds?.length > 0) {
            const sortedIds = [...selected.productIds].sort((a, b) => a - b);
            setProductIds(sortedIds);
            setSelectedProductIds(sortedIds);
        }
    }, [selected]);

    // predictions
    const productPredictionsFiltered = productPredictionsData?.allPredictions?.[productId] ?? [];
    const twelveNumbersArray = Array.from({ length: 12 }, (_, i) => i + 1);

    const uniqueYears = productPredictionsData?.monthSequences.allMonths ? 
        [...new Set(productPredictionsData?.monthSequences.allMonths.map(month => month.year))] : 
        [];

    const completeMonths = uniqueYears?.map(year => twelveNumbersArray
        .map(number => ({ 
            month: number, 
            year: year 
        }))
    ).flat() || [];

    const currentMonthIndex = completeMonths?.findIndex(
        month => month.month === latestForecast?.month && month.year === latestForecast?.year
    );

    /**Se removermos o currentMonthIndex + 1 o valor realizado do mês atual não será exibido */
    const pastMonths = completeMonths?.slice(0, currentMonthIndex + 1) || [];

    const isFirstMonthJanuary = (futureMonths) => futureMonths ? futureMonths.length > 0 && futureMonths[0].month === 1 : [];
    const initialNullMonthsQuantity = 
        isFirstMonthJanuary(productPredictionsData?.latestForecastPredictions[productId]) ? 
        0 : 
        latestForecast.month - 2;

    /** Se a condição de retorno for >= o mês corrente será tratado como mês futuro e o valor realizado até 
     * om momento não será exibido.
     */
    const isFutureMonth = (forecastMonthAndYear, predictionMonthAndYear) => {
        if (predictionMonthAndYear.year > forecastMonthAndYear.year) return true;
        if (predictionMonthAndYear.year < forecastMonthAndYear.year) return false;
        if (predictionMonthAndYear.year === forecastMonthAndYear.year)
            return predictionMonthAndYear.month > forecastMonthAndYear.month ? true : false;
    };

    const createLines = (productId) => {
        const futureMonthsData = defineFutureMonthsData.accuracyChart(
            productId, 
            productPredictionsData, 
            latestForecast, 
            productPredictionsFiltered
        );

        const usedColorsCount = futureMonthsData.length;
        
        const pastMonthsWithRealizedValue = futureLinesDefition.accuracyChart(pastMonths, productPredictionsFiltered)
        const preResult = cutInto12MonthsArrays([...pastMonthsWithRealizedValue]);
        
        const realizedSeries = preResult.map((arr, index) => {
        const values = [];
        const hoje = new Date();
        const mesAtual = hoje.getMonth() + 1;
        const anoAtual = hoje.getFullYear();
        const reverseIndex = preResult.length - 1 - index;
        
        arr.forEach(el => {
            if (!isFutureMonth(latestForecast, el)) {
            if (el.month === mesAtual && el.year === anoAtual) {
                values.push({
                y: el.realized,
                marker: {
                    enabled: true,
                    symbol: 'diamond',
                    radius: 6,
                    lineColor: PREDICTON_COLORS[usedColorsCount + reverseIndex],
                    lineWidth: 2
                }
                });
            } else {
                values.push(el.realized);
            }
            }
        });

        return {
            label: `${t("realized")} ${arr[0].year}`,
            marker: false,
            values: values,
            visible: index === preResult.length - 1,
            color: PREDICTON_COLORS[usedColorsCount + reverseIndex]
        };
        });

        const orderedRealizedSeries = realizedSeries.reverse();

        return futureMonthsData.concat(orderedRealizedSeries);
    };

    const defineLinesPredictionChartResult = futureLinesDefition.predictionChart(
        productPredictionsData, enableAdvancedLines, clientPlanValuesFromPastMonths,
        true, predictionChartLocation
    );

    return {
        actions: {
            setSelectedValue,
            setSelectedProductIds,
            setFreezeColumns
        },
        ctx: {
            // products
            groupFiltersAndDriversOptions,
            products: products,

            selected,
            selectedValue,
            productIds,
            selectedProductIds: selectedProductIds,
            generationDate,
            groupingName,

            // predictions data:
            forecastAssertivenessMonths: fetchLastSixMonthsPredictions?.forecastAssertivenessMonths || [],
            predictionsForAssertivenessAnalysis: fetchLastSixMonthsPredictions?.predictionsForAssertivenessAnalysis || [],
            latestForecast: latestForecast,

            indexes: fetchConsolidatedIndexes,

            consolidatedData: fetchConsolidatedPredictions,

            adjustConfidenceIntervalToLatestYear: adjustConfidenceIntervalToLatestYear(productPredictionsData?.confidenceIntervalMap),
            createLines: createLines(productId),

            defineLinesPredictionChart: defineLinesPredictionChartResult
        }, 
        uiState: {
            // products
            isLoadingProducts: isLoadingProducts,

            isLoadingForecast,
            
            freezeColumns,

            // predictions data
            isLoadingFetchLast6MonthsPredictions: isLoading,
            isLoadingProductPredictions: isLoadingProductPredictions,
            isLoadingIndexes: isLoadingIndexes,
            isLoadingConsolidatedPredictions: isLoadingConsolidatedPredictions,

            colors: PREDICTON_COLORS,
            xLabels: xLabels,
            xLabelsPredictionChart: productPredictionsData?.xLabels,
            lastHistoricMonthLabel: xLabels[initialNullMonthsQuantity],
            lastHistoricMonthLabelPredictionChart: productPredictionsData?.xLabels.at?.(-1) || "",
            ranges: productPredictionsData?.confidenceInterval || []            
        }
    }
}